import { gtmSendEvent } from "@wojo/analytics";
import { useI18n } from "@wojo/localization";
import { departuresFilterToFacet } from "@wojo/services";
import { Button, Form, FormCalendarSelect, FormCheckboxGroup } from "@wojo/ui";
import dayjs from "dayjs";
import React from "react";
import { useForm } from "react-hook-form";
import { useDeparturesStepContext } from "../context/DeparturesStepContext";
import { useTourPageContext } from "../../../_client/context";
import get from "lodash/get";

export const DepartureFiltersContent: React.FC = () => {
    const { productPage } = useI18n("productPage");
    const { pageConfig } = useTourPageContext();
    const { departures, setIsFilterOpen, filterStates, setFilterStates } =
        useDeparturesStepContext();
    const { disjunctiveFacets: facets } = departures;
    const { departureStartDate, departureEndDate, filters, tripTypes } =
        filterStates;

    const form = useForm({
        defaultValues: {
            tripTypes: tripTypes || [],
            departureDate: {
                ...(departureStartDate
                    ? { from: departureStartDate }
                    : undefined),
                ...(departureEndDate ? { to: departureEndDate } : undefined),
            },
            filters: filters || [],
        },
    });

    const filterFacetItems =
        pageConfig.departuresFilters?.map((filter) => {
            const count = () => {
                switch (filter) {
                    case "isOnSale":
                        return (
                            (
                                facets.find(
                                    (facet) => facet.name === "isOnSale",
                                )?.data as Record<string, number> | undefined
                            )?.["true"] ?? 0
                        );
                    case "status":
                        return (
                            (
                                facets.find((facet) => facet.name === "status")
                                    ?.data as Record<string, number> | undefined
                            )?.["Available"] ?? 0
                        );
                    case "hasExtensionAvailable":
                        return (
                            (
                                facets.find(
                                    (facet) =>
                                        facet.name === "hasExtensionAvailable",
                                )?.data as Record<string, number> | undefined
                            )?.["true"] ?? 0
                        );
                }
            };
            return {
                label: `${get(
                    productPage.tripBuilder.filters,
                    filter,
                )?.l()} (${count()})`,
                value: filter,
            };
        }) || [];

    const tripTypeDescriptions: Record<string, string | null> = {
        Plus:
            productPage.tripBuilder.filters.tripTypes?.plus.shortDescription.l() ||
            "",
        Essential:
            productPage.tripBuilder.filters.tripTypes?.essential.shortDescription.l() ||
            "",
    };

    const tripTypeList =
        facets.find(
            (facet) => facet.name === departuresFilterToFacet["tripType"],
        )?.data ?? {};

    const tripTypeFacetItems = Object.entries(tripTypeList).map(
        ([key, value]) => ({
            label: `${key} (${value})`,
            value: key,
            labelDetail: tripTypeDescriptions[key],
        }),
    );

    return (
        <Form
            form={form}
            style={{
                padding: "0 var(--g-spacing-md) var(--g-spacing-lg)",
                display: "grid",
                gap: "var(--g-spacing-lg)",
            }}
            onSubmit={(_, event) => {
                event?.preventDefault();
                const selectedFilters = filters?.map((filter) =>
                    get(productPage.tripBuilder.filters, filter)?.l(),
                );
                if (!!departureStartDate || !!departureEndDate) {
                    selectedFilters?.push("Departure Dates (Calendar)");
                }

                gtmSendEvent({
                    event: "track",
                    ga4Tracking: {
                        ga4_object: "TRIP_BUILDER",
                        ga4_action: "FILTERED",
                        filterType: "Trip Builder Tour Dates",
                        filteredBy: selectedFilters?.join("|") || "",
                        filterTripType: tripTypes?.join("|") || "",
                    },
                });
                setIsFilterOpen?.(false);
            }}
            onReset={async () => {
                await setFilterStates({
                    departureEndDate: null,
                    departureStartDate: null,
                    filters: null,
                    tripTypes: null,
                });
                form.reset({
                    tripTypes: [],
                    filters: [],
                    departureDate: {
                        from: undefined,
                        to: undefined,
                    },
                });
            }}
        >
            <FormCheckboxGroup
                items={filterFacetItems}
                name="filters"
                onChange={async (e) => {
                    const newFilters = e.target.checked
                        ? [...(filters ?? []), e.target.value]
                        : filters?.filter((f: string) => f !== e.target.value);

                    await setFilterStates({
                        filters: newFilters?.length ? newFilters : null,
                    });
                }}
            />
            {!!tripTypeFacetItems.length && (
                <FormCheckboxGroup
                    title="Trip type"
                    detailPosition="below"
                    items={tripTypeFacetItems}
                    onChange={async (e) => {
                        const newTripTypes = e.target.checked
                            ? [...(tripTypes ?? []), e.target.value]
                            : tripTypes?.filter(
                                  (f: string) => f !== e.target.value,
                              );

                        await setFilterStates({
                            tripTypes: newTripTypes?.length
                                ? newTripTypes
                                : null,
                        });
                    }}
                    name="tripTypes"
                />
            )}
            <FormCalendarSelect
                dayPickerOptions={{
                    fromDate: dayjs.utc().toDate(),
                    toDate: dayjs.utc().add(3, "year").toDate(),
                }}
                isRangeSelect
                label="Trip departure date"
                name="departureDate"
                onClick={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "OPEN_CALENDAR",
                        },
                    });
                }}
                onNavigate={(_, name) => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "CALENDAR_USE",
                            eventAction: `Paginate ${name}`,
                        },
                    });
                }}
                onReset={() => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "CALENDAR_USE",
                            eventAction: "Reset Dates",
                        },
                    });
                }}
                onSelected={async (date) => {
                    gtmSendEvent({
                        event: "track",
                        ga4Tracking: {
                            ga4_object: "TRIP_BUILDER",
                            ga4_action: "CALENDAR_USE",
                            eventAction: "Select Day",
                        },
                    });
                    if (date instanceof Date) {
                        return;
                    }
                    await setFilterStates({
                        departureStartDate: date?.from || null,
                        departureEndDate: date?.to || null,
                    });
                }}
                placeholder="Select a date"
            />
            <Button
                type="submit"
                style={{
                    justifySelf: "start",
                }}
            >
                Show filtered dates ({departures.nbHits})
            </Button>
            {(filters?.length ||
                tripTypes?.length ||
                departureStartDate ||
                departureEndDate) && (
                <Button
                    style={{
                        justifySelf: "start",
                    }}
                    type="reset"
                    variant="quiet"
                    onClick={async () => {
                        gtmSendEvent({
                            event: "track",
                            ga4Tracking: {
                                ga4_object: "TRIP_BUILDER",
                                ga4_action: "CLEAR_ALL_FILTERS",
                                filterType: "Trip Builder Tour Dates",
                            },
                        });
                    }}
                >
                    Clear filters
                </Button>
            )}
        </Form>
    );
};
